import React, { useState, useEffect, useRef } from 'react';
import API from '../../api';
import { Save, Image as ImageIcon, Plus, Trash2, Loader2, Check, AlertCircle, Eye, ChevronDown, ChevronRight, RotateCcw, Phone, Layers, Sparkles, HelpCircle, Globe, GalleryHorizontal } from 'lucide-react';
import { DEFAULT_CONTENT } from '../../landing/useLandingContent';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SERVIZIO = { id: '', name: '', description: '', bullets: [], image: '' };
const DEFAULT_FAQ = { q: '', a: '' };
const DEFAULT_GALLERIA_ITEM = { url: '', alt: '', tag: '' };

function resolveUrl(u) {
  if (!u) return '';
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  if (u.startsWith('/')) return `${BACKEND}${u}`;
  return u;
}

export default function LandingCMSTab() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [openSection, setOpenSection] = useState('contatti');

  useEffect(() => {
    API.get('/api/landing/content')
      .then(r => {
        // Merge soft con defaults per garantire tutti i campi
        setContent({ ...DEFAULT_CONTENT, ...(r.data || {}),
          company: { ...DEFAULT_CONTENT.company, ...(r.data?.company || {}) },
          hero: { ...DEFAULT_CONTENT.hero, ...(r.data?.hero || {}) },
          chi_siamo: { ...DEFAULT_CONTENT.chi_siamo, ...(r.data?.chi_siamo || {}) },
          servizi: r.data?.servizi || DEFAULT_CONTENT.servizi,
          faqs: r.data?.faqs || DEFAULT_CONTENT.faqs,
        });
      })
      .catch(() => setContent(DEFAULT_CONTENT))
      .finally(() => setLoading(false));
  }, []);

  const update = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]] = obj[keys[i]] ?? {};
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await API.put('/api/landing/content', content);
      setFeedback({ type: 'success', msg: '✓ Modifiche salvate! Le vedrai online entro 60 secondi (cache).' });
    } catch (e) {
      // Estrai un messaggio più dettagliato per facilitare il debug in produzione
      const status = e.response?.status;
      const backendDetail = e.response?.data?.detail || e.response?.data?.message;
      const networkMsg = e.message;
      let msg;
      if (status === 401 || status === 403) {
        msg = `Sessione scaduta o permessi insufficienti (HTTP ${status}). Esci e rientra come admin.`;
      } else if (status === 413) {
        msg = `Payload troppo grande (HTTP 413). Riduci le immagini base64 inline o riconverti in upload.`;
      } else if (status >= 500) {
        msg = `Errore server (HTTP ${status}): ${backendDetail || networkMsg || 'sconosciuto'}`;
      } else if (status) {
        msg = `Salvataggio fallito (HTTP ${status}): ${backendDetail || networkMsg}`;
      } else {
        // Nessun status → errore di rete/CORS/proxy
        msg = `Errore di rete: ${networkMsg}. Controlla connessione e/o configurazione proxy.`;
      }
      setFeedback({ type: 'error', msg });
      // Log completo in console per debug
      console.error('[LandingCMS save] failed:', { status, backendDetail, networkMsg, error: e });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 10000);
    }
  };

  const resetSection = (section) => {
    if (!window.confirm(`Ripristinare i valori di default per la sezione "${section}"?`)) return;
    update(section, JSON.parse(JSON.stringify(DEFAULT_CONTENT[section])));
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Caricamento contenuti…</div>;
  }

  return (
    <div className="space-y-4" data-testid="landing-cms-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
        <Globe className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
        <div className="flex-1">
          <p className="font-bold text-amber-900">Editor del sito pubblico (landing page)</p>
          <p className="text-sm text-amber-800 mt-1">
            Modifica testi, contatti, foto, video, servizi e FAQ. Le modifiche sono visibili su <a href="/" target="_blank" rel="noreferrer" className="underline font-bold">edilcima.it</a> entro 60 secondi.
            Lascia un campo vuoto per usare il valore di default.
          </p>
        </div>
        <a href="/" target="_blank" rel="noreferrer"
          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded inline-flex items-center gap-1.5 flex-shrink-0"
          data-testid="landing-preview-btn">
          <Eye size={14} /> Anteprima
        </a>
      </div>

      {feedback && (
        <div className={`p-3 rounded-md text-sm font-semibold flex items-center gap-2 ${feedback.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {feedback.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />} {feedback.msg}
        </div>
      )}

      {/* CONTATTI */}
      <Section title="Contatti & Azienda" icon={Phone} id="contatti" open={openSection === 'contatti'} onToggle={() => setOpenSection(openSection === 'contatti' ? null : 'contatti')}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Telefono fisso" value={content.company?.phoneFisso || ''} onChange={v => update('company.phoneFisso', v)} testid="cms-phone-fisso" />
          <Field label="Email principale" value={content.company?.email || ''} onChange={v => update('company.email', v)} testid="cms-email" />
          <Field label="Cellulare Germano" value={content.company?.phoneGermano || ''} onChange={v => update('company.phoneGermano', v)} />
          <Field label="Cellulare Simone" value={content.company?.phoneSimone || ''} onChange={v => update('company.phoneSimone', v)} />
          <Field label="PEC" value={content.company?.pec || ''} onChange={v => update('company.pec', v)} />
          <Field label="Orari" value={content.company?.hours || ''} onChange={v => update('company.hours', v)} placeholder="Lun-Ven 8:00-18:00" />
          <Field label="Indirizzo" value={content.company?.address?.street || ''} onChange={v => update('company.address.street', v)} />
          <div className="grid grid-cols-3 gap-2">
            <Field label="CAP" value={content.company?.address?.cap || ''} onChange={v => update('company.address.cap', v)} />
            <Field label="Città" value={content.company?.address?.city || ''} onChange={v => update('company.address.city', v)} />
            <Field label="Prov." value={content.company?.address?.province || ''} onChange={v => update('company.address.province', v)} />
          </div>
          <Field label="P.IVA" value={content.company?.vat || ''} onChange={v => update('company.vat', v)} />
          <Field label="Codice SDI" value={content.company?.sdiCode || ''} onChange={v => update('company.sdiCode', v)} />
        </div>
      </Section>

      {/* HERO */}
      <Section title="Hero (prima schermata)" icon={Sparkles} id="hero" open={openSection === 'hero'} onToggle={() => setOpenSection(openSection === 'hero' ? null : 'hero')} onReset={() => resetSection('hero')}>
        <div className="space-y-3">
          <Field label="Badge in alto" value={content.hero?.badge || ''} onChange={v => update('hero.badge', v)} />
          <div className="grid sm:grid-cols-3 gap-2">
            <Field label="Titolo riga 1" value={content.hero?.title_line1 || ''} onChange={v => update('hero.title_line1', v)} />
            <Field label="Titolo riga 2" value={content.hero?.title_line2 || ''} onChange={v => update('hero.title_line2', v)} />
            <Field label="Titolo riga 3 (arancio)" value={content.hero?.title_line3 || ''} onChange={v => update('hero.title_line3', v)} />
          </div>
          <Textarea label="Sottotitolo" value={content.hero?.subtitle || ''} onChange={v => update('hero.subtitle', v)} rows={3} />
          <Field label="CTA principale" value={content.hero?.cta_primary || ''} onChange={v => update('hero.cta_primary', v)} />
          <ImageField label="Immagine di sfondo" value={content.hero?.background_image || ''} onChange={v => update('hero.background_image', v)} />
          <Field label="URL video di sfondo (YouTube/Vimeo/MP4) — opzionale"
            value={content.hero?.video_url || ''}
            onChange={v => update('hero.video_url', v)}
            placeholder="https://www.youtube.com/watch?v=..." />
          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2">Statistiche (3 max)</label>
            <div className="grid sm:grid-cols-3 gap-2">
              {(content.hero?.stats || []).map((s, i) => (
                <div key={i} className="border border-slate-200 rounded p-2 space-y-1.5 bg-slate-50">
                  <input value={s.value || ''} onChange={e => {
                    const stats = [...(content.hero?.stats || [])];
                    stats[i] = { ...stats[i], value: e.target.value };
                    update('hero.stats', stats);
                  }} className="w-full px-2 py-1 text-sm border border-slate-300 rounded" placeholder="es. 25+ oppure 'auto'" />
                  <input value={s.label || ''} onChange={e => {
                    const stats = [...(content.hero?.stats || [])];
                    stats[i] = { ...stats[i], label: e.target.value };
                    update('hero.stats', stats);
                  }} className="w-full px-2 py-1 text-xs border border-slate-300 rounded" placeholder="Etichetta" />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Suggerimento: usa il valore "auto" per calcolare automaticamente gli anni di esperienza dal {content.company?.foundingYear}.</p>
          </div>
        </div>
      </Section>

      {/* CHI SIAMO */}
      <Section title="Chi Siamo" icon={Layers} id="chi_siamo" open={openSection === 'chi_siamo'} onToggle={() => setOpenSection(openSection === 'chi_siamo' ? null : 'chi_siamo')}>
        <Textarea label="Paragrafo (vuoto = testo di default)" value={content.chi_siamo?.paragrafo || ''} onChange={v => update('chi_siamo.paragrafo', v)} rows={6} />
        <ImageField label="Foto laterale" value={content.chi_siamo?.foto || ''} onChange={v => update('chi_siamo.foto', v)} />
      </Section>

      {/* SERVIZI */}
      <Section title={`Servizi (${(content.servizi || []).length})`} icon={Layers} id="servizi"
        open={openSection === 'servizi'} onToggle={() => setOpenSection(openSection === 'servizi' ? null : 'servizi')}
        onReset={() => resetSection('servizi')}
        action={
          <button onClick={() => update('servizi', [...(content.servizi || []), { ...DEFAULT_SERVIZIO, id: `srv-${Date.now()}` }])}
            className="text-xs font-bold px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 inline-flex items-center gap-1">
            <Plus size={12} /> Servizio
          </button>
        }
      >
        <div className="space-y-3">
          {(content.servizi || []).map((s, i) => (
            <div key={i} className="border border-slate-200 rounded p-3 bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-500">Servizio {i + 1}</span>
                <button onClick={() => update('servizi', content.servizi.filter((_, j) => j !== i))}
                  className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Field label="Nome" value={s.name || ''} onChange={v => {
                  const arr = [...content.servizi]; arr[i] = { ...arr[i], name: v }; update('servizi', arr);
                }} />
                <Field label="ID slug" value={s.id || ''} onChange={v => {
                  const arr = [...content.servizi]; arr[i] = { ...arr[i], id: v }; update('servizi', arr);
                }} />
              </div>
              <Textarea label="Descrizione" value={s.description || ''} rows={2} onChange={v => {
                const arr = [...content.servizi]; arr[i] = { ...arr[i], description: v }; update('servizi', arr);
              }} />
              <Field label="Bullets (separati da virgola)" value={(s.bullets || []).join(', ')} onChange={v => {
                const arr = [...content.servizi]; arr[i] = { ...arr[i], bullets: v.split(',').map(b => b.trim()).filter(Boolean) }; update('servizi', arr);
              }} />
              <ImageField label="Immagine" value={s.image || ''} onChange={v => {
                const arr = [...content.servizi]; arr[i] = { ...arr[i], image: v }; update('servizi', arr);
              }} />
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section title={`FAQ (${(content.faqs || []).length})`} icon={HelpCircle} id="faqs"
        open={openSection === 'faqs'} onToggle={() => setOpenSection(openSection === 'faqs' ? null : 'faqs')}
        onReset={() => resetSection('faqs')}
        action={
          <button onClick={() => update('faqs', [...(content.faqs || []), { ...DEFAULT_FAQ }])}
            className="text-xs font-bold px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 inline-flex items-center gap-1">
            <Plus size={12} /> FAQ
          </button>
        }
      >
        <div className="space-y-2">
          {(content.faqs || []).map((f, i) => (
            <div key={i} className="border border-slate-200 rounded p-3 bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-500">FAQ {i + 1}</span>
                <button onClick={() => update('faqs', content.faqs.filter((_, j) => j !== i))}
                  className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
              </div>
              <Field label="Domanda" value={f.q || ''} onChange={v => {
                const arr = [...content.faqs]; arr[i] = { ...arr[i], q: v }; update('faqs', arr);
              }} />
              <Textarea label="Risposta" value={f.a || ''} rows={2} onChange={v => {
                const arr = [...content.faqs]; arr[i] = { ...arr[i], a: v }; update('faqs', arr);
              }} />
            </div>
          ))}
        </div>
      </Section>

      {/* GALLERIA */}
      <Section title={`Galleria progetti (${(content.galleria || []).length})`} icon={GalleryHorizontal} id="galleria"
        open={openSection === 'galleria'} onToggle={() => setOpenSection(openSection === 'galleria' ? null : 'galleria')}
        action={
          <button onClick={() => update('galleria', [...(content.galleria || []), { ...DEFAULT_GALLERIA_ITEM }])}
            className="text-xs font-bold px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 inline-flex items-center gap-1">
            <Plus size={12} /> Foto
          </button>
        }
      >
        <p className="text-xs text-slate-500 mb-3">Se la galleria è vuota, viene mostrata quella di default. La prima foto occupa il riquadro grande, le altre le 5 caselle minori.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(content.galleria || []).map((g, i) => (
            <div key={i} className="border border-slate-200 rounded p-3 bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-500">Foto {i + 1}{i === 0 && ' (in evidenza)'}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => {
                    if (i === 0) return;
                    const arr = [...content.galleria]; [arr[i-1], arr[i]] = [arr[i], arr[i-1]]; update('galleria', arr);
                  }} disabled={i === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Sposta su">↑</button>
                  <button onClick={() => {
                    if (i === content.galleria.length - 1) return;
                    const arr = [...content.galleria]; [arr[i+1], arr[i]] = [arr[i], arr[i+1]]; update('galleria', arr);
                  }} disabled={i === content.galleria.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Sposta giù">↓</button>
                  <button onClick={() => update('galleria', content.galleria.filter((_, j) => j !== i))}
                    className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </div>
              </div>
              <ImageField label="Foto" value={g.url || ''} onChange={v => {
                const arr = [...content.galleria]; arr[i] = { ...arr[i], url: v }; update('galleria', arr);
              }} />
              <Field label="Didascalia (alt SEO)" value={g.alt || ''} onChange={v => {
                const arr = [...content.galleria]; arr[i] = { ...arr[i], alt: v }; update('galleria', arr);
              }} placeholder="Es: Ristrutturazione bagno 8mq a Calcinato" />
              <Field label="Tag/categoria" value={g.tag || ''} onChange={v => {
                const arr = [...content.galleria]; arr[i] = { ...arr[i], tag: v }; update('galleria', arr);
              }} placeholder="Es: Ristrutturazioni" />
            </div>
          ))}
        </div>
      </Section>

      {/* SAVE BAR sticky */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-4 px-4 py-3 flex items-center justify-between shadow-lg z-10">
        <p className="text-xs text-slate-500">Le modifiche sono visibili pubblicamente entro 60 secondi.</p>
        <button onClick={save} disabled={saving}
          className="px-6 py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary-hover disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="cms-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Salvataggio…' : 'Salva modifiche'}
        </button>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────

function Section({ title, icon: Icon, id, open, onToggle, onReset, action, children }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <div className="flex items-center justify-between p-3 hover:bg-slate-50">
        <button onClick={onToggle} className="flex items-center gap-2 text-slate-800 font-bold flex-1 text-left" data-testid={`cms-section-${id}`}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Icon size={16} className="text-primary" />
          {title}
        </button>
        <div className="flex items-center gap-2">
          {action}
          {onReset && (
            <button onClick={onReset} title="Ripristina default" className="text-xs text-slate-400 hover:text-primary">
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      </div>
      {open && <div className="p-4 border-t border-slate-200">{children}</div>}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary/40" />
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows}
        className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary/40 resize-y" />
    </div>
  );
}

function ImageField({ label, value, onChange }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      alert('Immagine troppo grande (max 8 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await API.post('/api/landing/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (e) {
      alert('Errore upload: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">{label}</label>
      <div className="flex gap-2 items-start">
        {value && (
          <img src={resolveUrl(value)} alt="anteprima" className="w-20 h-20 object-cover rounded border border-slate-200 flex-shrink-0" />
        )}
        <div className="flex-1 space-y-1.5">
          <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
            placeholder="https://... oppure carica un file"
            className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-primary/40" />
          <input ref={ref} type="file" accept="image/*" className="hidden"
            onChange={e => handleUpload(e.target.files?.[0])} />
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
            className="text-xs px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded inline-flex items-center gap-1 disabled:opacity-50">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
            {uploading ? 'Caricamento…' : 'Carica file'}
          </button>
        </div>
      </div>
    </div>
  );
}
